import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Modal } from '@safetyhub/ui';
import { useToastStore } from '../stores/toast';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { formatDateTime } from '../utils/format';
import { OpsManualLink } from '../components/OpsManualLink';
import {
  OPS_CHANNEL_LABELS,
  OPS_INCIDENT_QUERY_KEYS,
  OPS_KIND_LABELS,
  OPS_SEVERITY_CLASSES,
  OPS_SEVERITY_LABELS,
  OPS_SEVERITY_ORDER,
  OPS_TASK_RUN_STATUS_CLASSES,
  OPS_TASK_RUN_STATUS_LABELS,
  opsIncidentsApi,
  type ListOpsIncidentsParams,
  type OpsIncidentKind,
  type OpsIncidentSeverity,
  type OpsTaskRunRow,
} from '../lib/opsIncidents';
// 통보 기록 한 줄의 문구 판정 — 서버 선점 문구와 한 벌(shared)이다. `lib/opsIncidents`를 거치지 않는다(그 모듈은 spec이 vi.mock한다)
import { describeOpsDelivery } from '@safetyhub/shared';

/**
 * 운영 사건 — 자동화 작업(예약 작업·수집·가공)의 실패 목록·상세·확인 처리 + 예약 작업 실행 기록.
 * 서버: `admin/ops-incidents`(읽기·확인은 `monitoring-view`).
 * 통보 메일·웹훅의 링크가 `/ops-incidents/:id`로 오므로 그 경로로 열면 상세 모달이 바로 뜬다.
 */
export default function OpsIncidents() {
  const { id: routeId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'incidents' | 'task-runs'>('incidents');
  const [params, setParams] = useState<ListOpsIncidentsParams>({ status: 'open', page: 1, limit: 20 });

  const { data: summary } = useQuery({
    queryKey: OPS_INCIDENT_QUERY_KEYS.summary,
    queryFn: () => opsIncidentsApi.summary(),
    refetchInterval: 60_000,
  });

  const openDetail = (id: string) => navigate(`/ops-incidents/${id}`);
  const closeDetail = () => navigate('/ops-incidents');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">운영 사건</h2>
          <p className="text-sm text-gray-500">
            예약 작업·자동 수집·가공의 실패와 「예정대로 안 돈 작업」을 모읍니다. 같은 일이 반복되면 한 사건의 횟수만 올라갑니다.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <OpsManualLink topic="incidentReview" />
          <button
            type="button"
            onClick={() => navigate('/ops-alert-settings')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            내 알림 설정
          </button>
        </div>
      </div>

      {summary?.detector.stale && (
        <div className="p-3 rounded border border-red-300 bg-red-50 text-sm text-red-800">
          안 돈 작업 감지기가 {summary.detector.thresholdMinutes}분 넘게 성공하지 못했습니다
          {summary.detector.lastSucceededAt ? ` (마지막 성공 ${formatDateTime(summary.detector.lastSucceededAt)})` : ' (성공 기록 없음)'}
          . 이 상태에서는 「예정대로 안 돈 작업」 사건이 만들어지지 않습니다 — 서버의 예약 작업 실행을 확인하세요.
        </div>
      )}

      {summary && (
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="px-2 py-1 rounded bg-white border border-gray-200">미확인 {summary.openCount}건</span>
          {OPS_SEVERITY_ORDER.slice()
            .reverse()
            .filter((s) => summary.openBySeverity[s] > 0)
            .map((s) => (
              <span key={s} className={`px-2 py-1 rounded ${OPS_SEVERITY_CLASSES[s]}`}>
                {OPS_SEVERITY_LABELS[s]} {summary.openBySeverity[s]}
              </span>
            ))}
        </div>
      )}

      <div className="flex gap-1 border-b border-gray-200">
        {(
          [
            ['incidents', '사건'],
            ['task-runs', '예약 작업 실행 기록'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${
              tab === key ? 'border-red-600 text-red-700 font-medium' : 'border-transparent text-gray-500'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'incidents' ? (
        <IncidentList params={params} setParams={setParams} onOpen={openDetail} />
      ) : (
        <TaskRunTable />
      )}

      {routeId && <IncidentDetailModal id={routeId} onClose={closeDetail} />}
    </div>
  );
}

function IncidentList({
  params,
  setParams,
  onOpen,
}: {
  params: ListOpsIncidentsParams;
  setParams: (p: ListOpsIncidentsParams) => void;
  onOpen: (id: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: OPS_INCIDENT_QUERY_KEYS.list(params),
    queryFn: () => opsIncidentsApi.list(params),
  });

  return (
    <Card>
      <div className="flex flex-wrap gap-2 mb-3 text-sm">
        <select
          aria-label="상태"
          value={params.status ?? 'open'}
          onChange={(e) => setParams({ ...params, status: e.target.value as ListOpsIncidentsParams['status'], page: 1 })}
          className="border border-gray-300 rounded px-2 py-1"
        >
          <option value="open">미확인</option>
          <option value="acknowledged">확인됨</option>
          <option value="all">전체</option>
        </select>
        <select
          aria-label="심각도"
          value={params.severity ?? ''}
          onChange={(e) =>
            setParams({ ...params, severity: (e.target.value || undefined) as OpsIncidentSeverity | undefined, page: 1 })
          }
          className="border border-gray-300 rounded px-2 py-1"
        >
          <option value="">심각도 전체</option>
          {OPS_SEVERITY_ORDER.map((s) => (
            <option key={s} value={s}>
              {OPS_SEVERITY_LABELS[s]}
            </option>
          ))}
        </select>
        <select
          aria-label="종류"
          value={params.kind ?? ''}
          onChange={(e) =>
            setParams({ ...params, kind: (e.target.value || undefined) as OpsIncidentKind | undefined, page: 1 })
          }
          className="border border-gray-300 rounded px-2 py-1"
        >
          <option value="">종류 전체</option>
          {(Object.keys(OPS_KIND_LABELS) as OpsIncidentKind[]).map((k) => (
            <option key={k} value={k}>
              {OPS_KIND_LABELS[k]}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500 py-6 text-center">불러오는 중…</p>
      ) : !data || data.items.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">해당하는 운영 사건이 없습니다.</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-2">심각도</th>
                <th className="py-2 pr-2">종류</th>
                <th className="py-2 pr-2">제목</th>
                <th className="py-2 pr-2">출처</th>
                <th className="py-2 pr-2 text-right">반복</th>
                <th className="py-2 pr-2">최근 발생</th>
                <th className="py-2">상태</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr
                  key={it.id}
                  onClick={() => onOpen(it.id)}
                  className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                >
                  <td className="py-2 pr-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${OPS_SEVERITY_CLASSES[it.severity]}`}>
                      {OPS_SEVERITY_LABELS[it.severity]}
                    </span>
                  </td>
                  <td className="py-2 pr-2 text-gray-600">{OPS_KIND_LABELS[it.kind]}</td>
                  <td className="py-2 pr-2 text-gray-900">{it.title}</td>
                  <td className="py-2 pr-2 text-gray-500 font-mono text-xs">{it.sourceKey}</td>
                  <td className="py-2 pr-2 text-right">{it.occurrenceCount}</td>
                  <td className="py-2 pr-2 text-gray-600">{formatDateTime(it.lastOccurredAt)}</td>
                  <td className="py-2 text-gray-600">
                    {it.acknowledgedAt ? `확인 (${it.acknowledgedBy?.name ?? '알 수 없음'})` : '미확인'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.meta.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-3 text-sm">
              <button
                type="button"
                disabled={(params.page ?? 1) <= 1}
                onClick={() => setParams({ ...params, page: (params.page ?? 1) - 1 })}
                className="px-2 py-1 border rounded disabled:opacity-40"
              >
                이전
              </button>
              <span className="px-2 py-1">
                {params.page ?? 1} / {data.meta.totalPages}
              </span>
              <button
                type="button"
                disabled={(params.page ?? 1) >= data.meta.totalPages}
                onClick={() => setParams({ ...params, page: (params.page ?? 1) + 1 })}
                className="px-2 py-1 border rounded disabled:opacity-40"
              >
                다음
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function IncidentDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [note, setNote] = useState('');
  const { data, isLoading, isError } = useQuery({
    queryKey: OPS_INCIDENT_QUERY_KEYS.detail(id),
    queryFn: () => opsIncidentsApi.get(id),
  });

  const ack = useMutation({
    mutationFn: () => opsIncidentsApi.acknowledge(id, note),
    onSuccess: () => {
      showToast('확인 처리했습니다.', 'success');
      queryClient.invalidateQueries({ queryKey: OPS_INCIDENT_QUERY_KEYS.all });
    },
    onError: (err) => showToast(apiErrorMessage(err, '확인 처리에 실패했습니다.'), 'error'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="운영 사건 상세"
      size="lg"
      footer={
        data && !data.acknowledgedAt ? (
          <div className="flex w-full items-center gap-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="확인 메모(선택)"
              maxLength={1000}
              className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
            <Button variant="primary" size="sm" disabled={ack.isPending} onClick={() => ack.mutate()}>
              확인 처리
            </Button>
          </div>
        ) : undefined
      }
    >
      {isLoading ? (
        <p className="text-sm text-gray-500">불러오는 중…</p>
      ) : isError || !data ? (
        <p className="text-sm text-red-600">사건을 불러오지 못했습니다.</p>
      ) : (
        <dl className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-xs ${OPS_SEVERITY_CLASSES[data.severity]}`}>
              {OPS_SEVERITY_LABELS[data.severity]}
            </span>
            <span className="text-gray-600">{OPS_KIND_LABELS[data.kind]}</span>
            <span className="font-medium text-gray-900">{data.title}</span>
          </div>
          <Row label="출처" value={<span className="font-mono text-xs">{data.sourceKey}</span>} />
          <Row label="반복" value={`${data.occurrenceCount}회`} />
          <Row label="처음 발생" value={formatDateTime(data.firstOccurredAt)} />
          <Row label="최근 발생" value={formatDateTime(data.lastOccurredAt)} />
          {data.acknowledgedAt && (
            <Row
              label="확인"
              value={`${formatDateTime(data.acknowledgedAt)} · ${data.acknowledgedBy?.name ?? '알 수 없음'}${
                data.acknowledgeNote ? ` · ${data.acknowledgeNote}` : ''
              }`}
            />
          )}
          <div>
            <dt className="text-gray-500 mb-1">내용(최근 발생 기준)</dt>
            <dd className="p-2 bg-gray-50 border border-gray-200 rounded whitespace-pre-wrap break-all">{data.message}</dd>
          </div>
          {data.context && (
            <div>
              <dt className="text-gray-500 mb-1">맥락</dt>
              <dd className="p-2 bg-gray-50 border border-gray-200 rounded text-xs font-mono whitespace-pre-wrap break-all max-h-60 overflow-auto">
                {JSON.stringify(data.context, null, 2)}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-gray-500 mb-1">채널별 통보 기록 (채널마다 하루 1회)</dt>
            <dd>
              {data.deliveries.length === 0 ? (
                <p className="text-gray-500">통보 기록이 없습니다 — 알림 채널을 켠 관리자가 없거나 최소 심각도에 못 미쳤습니다.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-1 pr-2">관리자</th>
                      <th className="py-1 pr-2">채널</th>
                      <th className="py-1 pr-2">날짜</th>
                      <th className="py-1 pr-2">결과</th>
                      <th className="py-1">시도</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.deliveries.map((d) => {
                      // 선점 행(FAILED + 「발송 중」 문구)을 실패로 그리지 않는다 — 리뷰 P1 ① (c)
                      const shown = describeOpsDelivery(d);
                      const toneClass =
                        shown.tone === 'sent' ? 'text-green-700' : shown.tone === 'pending' ? 'text-amber-700' : 'text-red-600';
                      return (
                        <tr key={d.id} className="border-b last:border-0">
                          <td className="py-1 pr-2">{d.superAdmin?.name ?? '(삭제됨)'}</td>
                          <td className="py-1 pr-2">{OPS_CHANNEL_LABELS[d.channel]}</td>
                          <td className="py-1 pr-2">{d.deliveryDate.slice(0, 10)}</td>
                          <td className={`py-1 pr-2 ${toneClass}`}>{shown.label}</td>
                          <td className="py-1">{d.attempts}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </dd>
          </div>
        </dl>
      )}
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500 shrink-0">{label}</dt>
      <dd className="text-gray-900 text-right">{value}</dd>
    </div>
  );
}

function formatInterval(minutes: number): string {
  if (minutes < 60) return `${minutes}분`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 6) / 10}시간`;
  return `${Math.round(minutes / 144) / 10}일`;
}

function TaskRunTable() {
  const { data, isLoading } = useQuery({
    queryKey: OPS_INCIDENT_QUERY_KEYS.taskRuns,
    queryFn: () => opsIncidentsApi.taskRuns(),
  });

  if (isLoading) return <p className="text-sm text-gray-500 py-6 text-center">불러오는 중…</p>;
  if (!data) return null;

  return (
    <Card>
      <p className="text-xs text-gray-500 mb-2">
        배포로 서버가 바뀌어도 남는 기록입니다. 「낡음」은 마지막 성공(없으면 감시 시작) 뒤 예정 주기 + 여유를 넘긴
        작업이고, 「실패 중」은 아직 예정 안이지만 마지막 회차가 실패한 작업입니다. 주기 칸의 cron은 괄호 안 시간대로
        돌고, 다음 실행은 이 화면의 시각으로 보입니다. <OpsManualLink topic="taskRuns" className="text-brand-600 hover:underline" />
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b">
            <th className="py-2 pr-2">작업</th>
            <th className="py-2 pr-2">주기</th>
            <th className="py-2 pr-2">마지막 성공</th>
            <th className="py-2 pr-2">마지막 실패</th>
            <th className="py-2 pr-2 text-right">연속 실패</th>
            <th className="py-2">상태</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r: OpsTaskRunRow) => (
            <tr key={r.taskKey} className="border-b last:border-0 align-top">
              <td className="py-2 pr-2">
                <div className="text-gray-900">{r.label}</div>
                <div className="text-xs text-gray-400 font-mono">{r.taskKey}</div>
              </td>
              <td className="py-2 pr-2 text-gray-600">
                {formatInterval(r.expectedIntervalMinutes)}
                <div className="text-xs text-gray-400 font-mono">
                  {r.cron} ({r.effectiveTimeZone})
                </div>
                {r.nextRunAt && <div className="text-xs text-gray-500">다음 {formatDateTime(r.nextRunAt)}</div>}
              </td>
              <td className="py-2 pr-2 text-gray-600">{r.lastSucceededAt ? formatDateTime(r.lastSucceededAt) : '-'}</td>
              <td className="py-2 pr-2 text-gray-600">
                {r.lastFailedAt ? formatDateTime(r.lastFailedAt) : '-'}
                {r.lastError && <div className="text-xs text-red-600 break-all">{r.lastError}</div>}
              </td>
              <td className="py-2 pr-2 text-right">{r.consecutiveFailures}</td>
              <td className="py-2">
                {/* 상태는 서버가 정한다(`status` — 낡음·기록 없음·실패 중·정상). 종전엔 `stale`만 그려 낡지 않은 채
                    실패만 반복하는 작업이 「정상」으로 보였다(리뷰 O4) */}
                <span className={`px-2 py-0.5 rounded text-xs ${OPS_TASK_RUN_STATUS_CLASSES[r.status]}`}>
                  {OPS_TASK_RUN_STATUS_LABELS[r.status]}
                  {r.status === 'failing' && ` ${r.consecutiveFailures}회`}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
